import React, { useState, useEffect, useRef } from 'react';
import FuzzyProjectSelect from './FuzzyProjectSelect';
import AiAnalysisCard from './MonitorComponents/AiAnalysisCard';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';

const getToken = () => localStorage.getItem('notes_token') || '';
const fetchWithAuth = (url, options = {}) => {
  return fetch(url, { ...options, headers: { ...options.headers, 'x-token': getToken() } });
};

const COLORS_4 = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];
const COLORS_6 = ['#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981'];
const TIME_POINT_LABELS = ['09:30', '11:00', '14:00', '17:00'];

const LIGHT_COLORS = {
  green: { border: '#52c41a', bg: '#f6ffed', text: '#389e0d', emoji: '🟢', label: '快' },
  yellow: { border: '#faad14', bg: '#fffbe6', text: '#d48806', emoji: '🟡', label: '一般' },
  red: { border: '#ff4d4f', bg: '#fff2f0', text: '#cf1322', emoji: '🔴', label: '慢' },
  alert: { border: '#ff4d4f', bg: '#fff1f0', text: '#a8071a', emoji: '⚠️', label: '异常' },
  gray: { border: '#d9d9d9', bg: '#fafafa', text: '#999', emoji: '⚪', label: '无数据' }
};

export default function MonitorDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('monitor_lastProject');
    return saved || '';
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [todayStatus, setTodayStatus] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [responseDist, setResponseDist] = useState(null);
  const [monthlyAvgData, setMonthlyAvgData] = useState(null);
  const [monthlyAvgOnline, setMonthlyAvgOnline] = useState(null);
  const [monthlyTpAvg, setMonthlyTpAvg] = useState(null);
  const [config, setConfig] = useState({ scenarios: [], timePoints: [] });
  const weeklyChartRef = useRef(null);
  const onlineBarRef = useRef(null);
  const pieChartRef = useRef(null);
  const monthlyAvgRef = useRef(null);
  const monthlyAvgBarRef = useRef(null);
  const monthlyGroupBarRef = useRef(null);

  useEffect(() => { loadProjects(); }, []);
  useEffect(() => { if (selectedProject) loadConfig(); }, [selectedProject]);
  useEffect(() => { if (selectedProject) loadAllData(); }, [selectedProject, selectedDate]);

  const loadProjects = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/projects`);
      const data = await res.json();
      if (data.success) {
        setProjects(data.projects || []);
        if (!selectedProject && data.projects?.length > 0) {
          // 不再自动选第一个，留给 localStorage 处理
          // 如果 localStorage 也没有，就保持空，让用户选
        }
      }
    } catch (e) { console.error(e); }
  };

  const loadConfig = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/config/${selectedProject}`);
      const data = await res.json();
      if (data.success && data.config) setConfig({ ...data.config });
    } catch (e) { console.error(e); }
  };

  const loadAllData = async () => {
    console.log('[Monitor] loadAllData 被调用, selectedProject:', selectedProject, 'selectedDate:', selectedDate);
    if (!selectedProject) return;
    
    // 切换项目时先清空所有数据，避免显示其他项目的旧数据
    setTodayStatus(null);
    setComparison(null);
    setWeeklyData(null);
    setGridData(null);
    setResponseDist(null);
    setMonthlyAvgData(null);
    setMonthlyAvgOnline(null);
    setMonthlyTpAvg(null);
    
    setLoading(true);
    try {
      const params = selectedDate ? `?date=${selectedDate}` : '';
      const [r1, r2, r3, r4, r5, r6, r7, r8] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/monitor/scenario-status/${selectedProject}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/comparison/${selectedProject}`),
        fetchWithAuth(`${API_URL}/api/monitor/weekly/${selectedProject}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/traffic-grid/${selectedProject}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/response-distribution/${selectedProject}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/yearly-monthly-avg/${selectedProject}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/monthly-avg-online/${selectedProject}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/monthly-timepoint-avg/${selectedProject}${params}`)
      ]);
      const [d1, d2, d3, d4, d5, d6, d7, d8] = await Promise.all([r1.json(), r2.json(), r3.json(), r4.json(), r5.json(), r6.json(), r7.json(), r8.json()]);
      if (d1.success) setTodayStatus(d1);
      if (d2.success) setComparison(d2);
      if (d3.success) setWeeklyData(d3);
      if (d4.success) setGridData(d4);
      if (d5.success) setResponseDist(d5);
      if (d6.success) setMonthlyAvgData(d6);
      if (d7.success) setMonthlyAvgOnline(d7);
      if (d8.success) setMonthlyTpAvg(d8);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // 近7天趋势图 - 紫蓝色系
  useEffect(() => {
    if (!weeklyChartRef.current || !window.echarts || !weeklyData || !config.scenarios.length) return;

    var timer = setTimeout(function() {
      try {
        var chart = window.echarts.init(weeklyChartRef.current);
        var dates = weeklyData.dates || [];
        var timePoints = weeklyData.timePoints || [];
        var onlineByDate = weeklyData.onlineByDate || {};
        var responseByScenario = weeklyData.responseByScenario || [];
        var colors = ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6'];
        var lineColors = ['#1E40AF', '#7C3AED', '#059669', '#D97706'];

        // 在线人数柱状图
        var onlineSeries = [];
        for (var t = 0; t < timePoints.length; t++) {
          var tp = timePoints[t];
          var dataArr = [];
          for (var d = 0; d < dates.length; d++) {
            var dayData = onlineByDate[dates[d]];
            dataArr.push(dayData && dayData[tp] !== undefined ? dayData[tp] : 0);
          }
          onlineSeries.push({
            name: tp + ' 在线',
            type: 'bar',
            yAxisIndex: 0,
            data: dataArr,
            itemStyle: {
              color: colors[t % 4],
              borderRadius: [4, 4, 0, 0]
            },
            barMaxWidth: 28,
            barGap: '20%'
          });
        }

        // 场景响应折线
        var responseSeries = [];
        for (var s = 0; s < responseByScenario.length; s++) {
          var sc = responseByScenario[s];
          var dataArr2 = [];
          for (var d2 = 0; d2 < dates.length; d2++) {
            dataArr2.push(sc.data && sc.data[dates[d2]] !== undefined ? sc.data[dates[d2]] : null);
          }
          responseSeries.push({
            name: sc.name,
            type: 'line',
            yAxisIndex: 1,
            data: dataArr2,
            itemStyle: { color: lineColors[s % 4] },
            lineStyle: { width: 2.5, type: s === 0 ? 'solid' : 'dashed' },
            symbol: 'circle',
            symbolSize: 7,
            smooth: false
          });
        }

        chart.setOption({
          title: {
            text: '近7天趋势',
            left: 'center',
            top: 8,
            textStyle: { fontSize: 15, fontWeight: 600, color: '#1f2937' }
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross', crossStyle: { color: '#d1d5db' } },
            backgroundColor: '#fff',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            textStyle: { color: '#374151', fontSize: 13 },
            formatter: (ps) => {
              if (!ps || !ps.length) return '';
              const dateStr = dates[ps[0].dataIndex] || '';
              let h = `<div style="font-weight:600;margin-bottom:8px;color:#1f2937">${dateStr}</div>`;
              ps.forEach(p => {
                if (p.value != null) {
                  const unit = p.seriesName.includes('在线') ? '人' : 's';
                  h += `<div style="margin:4px 0"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px"></span>${p.seriesName}: <b>${p.value}${unit}</b></div>`;
                }
              });
              return h;
            }
          },
          legend: {
            bottom: 8,
            type: 'scroll',
            textStyle: { fontSize: 12, color: '#4b5563' },
            itemWidth: 14,
            itemHeight: 14,
            itemGap: 16
          },
          grid: { bottom: 52, left: 64, right: 64, top: 48 },
          xAxis: {
            type: 'category',
            data: dates.map(d => d.substring(5)),
            axisLabel: { fontSize: 11, color: '#6b7280' },
            axisTick: { alignWithLabel: true },
            axisLine: { lineStyle: { color: '#e5e7eb' } }
          },
          yAxis: [
            {
              type: 'value',
              name: '在线人数',
              position: 'left',
              nameTextStyle: { color: '#6b7280', fontSize: 11 },
              axisLine: { lineStyle: { color: '#3B82F6' } },
              splitLine: { lineStyle: { type: 'dashed', color: '#f3f4f6' } }
            },
            {
              type: 'value',
              name: '响应时间(s)',
              position: 'right',
              nameTextStyle: { color: '#6b7280', fontSize: 11 },
              axisLine: { lineStyle: { color: '#7C3AED' } },
              splitLine: { show: false }
            }
          ],
          series: [...onlineSeries, ...responseSeries]
        });

        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      } catch (e) { console.error(e); }
    }, 150);

    return () => clearTimeout(timer);
  }, [weeklyData, config]);

  // 清理ECharts实例
  useEffect(() => {
    return () => {
      if (weeklyChartRef.current) {
        try { window.echarts?.dispose(weeklyChartRef.current); } catch(e) {}
      }
    };
  }, []);

  // 在线人数柱状图 - echarts渲染
  useEffect(() => {
    if (!onlineBarRef.current || !window.echarts || !weeklyData) return;
    setTimeout(() => {
      try {
        var chart = window.echarts.init(onlineBarRef.current);
        var dates = weeklyData.dates || [];
        var timePoints = weeklyData.timePoints || ['09:00','11:00','14:00','16:00'];
        var onlineByDate = weeklyData.onlineByDate || {};
        var colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];
        var series = timePoints.map(function(tp, i) {
          var dataArr = dates.map(function(d) {
            var day = onlineByDate[d];
            return (day && day[tp] != null) ? day[tp] : 0;
          });
          return {
            name: tp + ' 在线',
            type: 'bar',
            data: dataArr,
            itemStyle: { color: colors[i % 4], borderRadius: [4, 4, 0, 0] },
            barMaxWidth: 24,
            barGap: '15%',
            label: { show: true, position: 'top', fontSize: 10, color: '#374151' }
          };
        });
        chart.setOption({
          title: { text: '', left: 'center', top: 8, textStyle: { fontSize: 13, color: '#1f2937' } },
          tooltip: { trigger: 'axis' },
          legend: { bottom: 0, textStyle: { fontSize: 11 }, selectedMode: 'multiple' },
          grid: { top: 15, bottom: 40, left: 50, right: 20 },
          xAxis: { type: 'category', data: dates.map(function(d){ return d.substring(5); }), axisLabel: { fontSize: 10 } },
          yAxis: { type: 'value', name: '在线人数', nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 10 } },
          series: series
        });
        window.addEventListener('resize', function(){ chart.resize(); });
      } catch(e) { console.error('[onlineBar]', e); }
    }, 100);
  }, [weeklyData]);

  // 响应时间分布饼图
  useEffect(() => {
    if (!pieChartRef.current || !window.echarts || !responseDist) return;
    setTimeout(function(){
      try {
        var chart = window.echarts.init(pieChartRef.current);
        var d = responseDist.distribution || {};
        var data = [
          { value: d.green || 0, name: '🟢 快', itemStyle: { color: '#52c41a' } },
          { value: d.yellow || 0, name: '🟡 一般', itemStyle: { color: '#faad14' } },
          { value: d.red || 0, name: '🔴 慢', itemStyle: { color: '#ff4d4f' } },
          { value: d.alert || 0, name: '⚠️ 异常', itemStyle: { color: '#722ed1' } }
        ];
        chart.setOption({
          tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
          legend: { bottom: 0, textStyle: { fontSize: 11 } },
          series: [{
            type: 'pie',
            radius: ['35%', '65%'],
            center: ['50%', '45%'],
            data: data,
            label: { show: true, formatter: '{b}\n{d}%', fontSize: 11 },
            emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.3)' } }
          }]
        });
        window.addEventListener('resize', function(){ chart.resize(); });
      } catch(e) { console.error('[pieChart]', e); }
    }, 100);
  }, [responseDist]);

  // 月度平均响应时间折线图
  useEffect(() => {
    if (!monthlyAvgRef.current || !window.echarts || !monthlyAvgData) return;
    setTimeout(function(){
      try {
        var chart = window.echarts.init(monthlyAvgRef.current);
        var months = monthlyAvgData.months || [];
        var scenarios = monthlyAvgData.scenarios || [];
        var lineColors = ['#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981'];
        var series = scenarios.map(function(sc, i) {
          return {
            name: sc.name,
            type: 'line',
            data: (sc.data || []).map(function(v){ return v != null ? Number(v) : null; }),
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2, color: lineColors[i % 6] },
            itemStyle: { color: lineColors[i % 6] },
            label: { show: true, position: 'top', fontSize: 10, formatter: function(p){ return p.value != null ? Number(p.value).toFixed(1) : ''; } }
          };
        });
        chart.setOption({
          title: { text: '', left: 'center', top: 8, textStyle: { fontSize: 15, fontWeight: 600, color: '#1f2937' } },
          tooltip: { trigger: 'axis', formatter: function(ps) {
            if(!ps||!ps.length) return '';
            var h = '<div style="font-weight:600;margin-bottom:4px">' + months[ps[0].dataIndex] + '</div>';
            ps.forEach(function(p){ if(p.value != null) h += '<div>' + p.marker + p.seriesName + ': <b>' + Number(p.value).toFixed(1) + 's</b></div>'; });
            return h;
          } },
          legend: { bottom: 0, textStyle: { fontSize: 11 }, type: 'scroll', itemWidth: 14, itemHeight: 14 },
          grid: { left: 55, right: 30, top: 10, bottom: 40 },
          xAxis: { type: 'category', data: months, axisLabel: { fontSize: 10 } },
          yAxis: { type: 'value', name: '响应时间(s)', nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 10 } },
          series: series
        });
        window.addEventListener('resize', function(){ chart.resize(); });
      } catch(e) { console.error('[monthlyAvg]', e); }
    }, 100);
  }, [monthlyAvgData]);

  // 全年各月日均在线（柱状图+平均线+趋势线）
  useEffect(() => {
    if (!monthlyAvgBarRef.current || !window.echarts || !monthlyAvgOnline) return;
    setTimeout(function(){
      try {
        var chart = window.echarts.init(monthlyAvgBarRef.current);
        var months = monthlyAvgOnline.months || [];
        var data = monthlyAvgOnline.avgOnline || [];
        var avgVal = data.length > 0 ? Math.round(data.reduce(function(a,b){ return a+b; }, 0) / data.length) : 0;
        chart.setOption({
          tooltip: { trigger: 'axis' },
          grid: { left: 55, right: 30, top: 15, bottom: 30 },
          xAxis: { type: 'category', data: months, axisLabel: { fontSize: 10 } },
          yAxis: { type: 'value', name: '在线人数', nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 10 } },
          series: [
            {
              name: '日均在线',
              type: 'bar',
              data: data,
              itemStyle: { color: '#3B82F6', borderRadius: [4,4,0,0] },
              barMaxWidth: 40,
              markLine: {
                silent: true,
                lineStyle: { color: '#EF4444', type: 'dashed', width: 2 },
                label: { formatter: avgVal + '人', fontSize: 10, color: '#EF4444' },
                data: [{ yAxis: avgVal }]
              }
            },
            {
              name: '趋势',
              type: 'line',
              data: data,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: { color: '#8B5CF6', width: 2 },
              itemStyle: { color: '#8B5CF6' },
              label: { show: true, position: 'top', fontSize: 10, color: '#8B5CF6' }
            }
          ]
        });
        window.addEventListener('resize', function(){ chart.resize(); });
      } catch(e) { console.error('[monthlyAvgBar]', e); }
    }, 100);
  }, [monthlyAvgOnline]);

  // 全年各时段平均在线（分组柱状图）
  useEffect(() => {
    if (!monthlyGroupBarRef.current || !window.echarts || !monthlyTpAvg) return;
    setTimeout(function(){
      try {
        var chart = window.echarts.init(monthlyGroupBarRef.current);
        var months = monthlyTpAvg.months || [];
        var timePoints = monthlyTpAvg.timePoints || ['09:00','11:00','14:00','16:00'];
        var tpData = monthlyTpAvg.data || {};
        var colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];
        var series = timePoints.map(function(tp, i) {
          return {
            name: tp + ' 在线',
            type: 'bar',
            barGap: '10%',
            data: (tpData[tp] || []),
            itemStyle: { color: colors[i % 4], borderRadius: [3,3,0,0] },
            label: { show: true, position: 'top', fontSize: 9 }
          };
        });
        chart.setOption({
          tooltip: { trigger: 'axis' },
          legend: { bottom: 0, textStyle: { fontSize: 11 } },
          grid: { left: 55, right: 20, top: 10, bottom: 40 },
          xAxis: { type: 'category', data: months, axisLabel: { fontSize: 10 } },
          yAxis: { type: 'value', name: '在线人数', nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 10 } },
          series: series
        });
        window.addEventListener('resize', function(){ chart.resize(); });
      } catch(e) { console.error('[monthlyGroupBar]', e); }
    }, 100);
  }, [monthlyTpAvg]);

  const fmt = (v) => v === null || v === undefined ? '—' : (typeof v === 'number' ? v.toLocaleString() : v);

  // 红绿灯网格图 - 纯HTML表格实现，去掉彩色竖线
  const buildGridTable = () => {
    if (!gridData) return null;
    const { dates, timePoints, scenarios, grid } = gridData;
    const cellMap = {};
    grid.forEach(g => { cellMap[`${g.date}_${g.timePoint}_${g.scenarioId}`] = g; });

    return (
      <div className="grid-table-wrapper">
        <table className="traffic-grid-table">
          <thead>
            <tr>
              <th rowSpan={2} className="scenario-header" style={{ borderRight: '2px solid #e5e7eb', verticalAlign: 'middle' }}>场景</th>
              {dates.map(d => (
                <th key={d} colSpan={timePoints.length} className="date-header">
                  {d.substring(5)}
                </th>
              ))}
            </tr>
            <tr>
              {dates.map(d => timePoints.map((tp, ti) => (
                <th key={`${d}_${tp}`} className="timepoint-header"
                    style={{ borderRight: (ti === timePoints.length - 1 && d !== dates[dates.length - 1]) ? '1px solid #e5e7eb' : 'none' }}>
                  {tp.substring(0, 2)}
                </th>
              )))}
            </tr>
          </thead>
          <tbody>
            {scenarios.map(sc => (
              <tr key={sc.id}>
                <td className="scenario-header">{sc.name}</td>
                {dates.map(d => timePoints.map(tp => {
                  const key = `${d}_${tp}_${sc.id}`;
                  const g = cellMap[key];
                  if (!g) return <td key={key} className="grid-cell empty"></td>;
                  const l = LIGHT_COLORS[g.light] || LIGHT_COLORS.gray;
                  return (
                    <td
                      key={key}
                      className="grid-cell"
                      style={{ backgroundColor: l.bg, borderRight: (tp === timePoints.length - 1 && d !== dates[dates.length - 1]) ? '1px solid #e5e7eb' : 'none' }}
                      title={`${sc.name} ${d} ${tp}\n响应: ${g.responseTime}s\n状态: ${l.label}`}
                    >
                      <span className="cell-emoji">{l.emoji}</span>
                      <span className="cell-time">{g.responseTime}s</span>
                    </td>
                  );
                }))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      {/* 页面标题 + 筛选器 + 操作按钮 - 复用工单看板样式 */}
      <div className="page-header sticky">
        <div className="page-title">
          <h1>🖥️ 运维监控看板</h1>
        </div>
        <div className="header-right">
          <div className="filter-bar">
            <div className="control-group">
              <label>项目</label>
              <FuzzyProjectSelect
                projects={projects}
                value={selectedProject}
                onChange={value => {
                  console.log('[Monitor] 切换项目:', value);
                  setSelectedProject(value);
                  if (value) localStorage.setItem('monitor_lastProject', value);
                }}
                placeholder="选择项目"
              />
            </div>
            <div className="control-group">
              <label>基准日期</label>
              <input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="filter-input"
              />
            </div>
            <button onClick={() => navigate(`/monitor/viewer/${selectedProject || ''}`)} className="btn-secondary" style={{ fontSize: "12px", padding: "6px 12px" }}>
              📋 数据编辑
            </button>
            <button onClick={() => navigate(`/monitor/config/${selectedProject || ''}`)} className="btn-secondary" style={{ fontSize: "12px", padding: "6px 12px" }}>
              ⚙️ 配置
            </button>
            <button onClick={() => navigate(`/monitor/layout/${selectedProject || ''}`)} className="btn-secondary" style={{ fontSize: "12px", padding: "6px 12px" }}>
              🎨 布局
            </button>
            <button onClick={() => navigate(`/monitor/data/${selectedProject || ''}`)} className="btn-primary" style={{ fontSize: "12px", padding: "6px 12px" }}>
              📝 录入
            </button>
            <button onClick={() => navigate(`/monitor/report/${selectedProject || ''}`)} className="btn-secondary" style={{ fontSize: "12px", padding: "6px 12px" }}>
              📄 生成报告
            </button>
          </div>
        </div>
      </div>

      {/* 页面头与内容区的间距 */}
      <div style={{ height: '20px' }}></div>

      {loading && <div className="loading-overlay"><div className="spinner"></div><span>加载中...</span></div>}

      {/* 无数据提示 */}
      {!loading && selectedProject && !todayStatus && !weeklyData && !gridData && (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <h3 style={{ color: '#666', marginBottom: '8px' }}>暂无监控数据</h3>
          <p>该项目尚未配置监控，或暂无录入数据</p>
          <div style={{ marginTop: '20px' }}>
            <button onClick={() => navigate(`/monitor/config/${selectedProject}`)} className="btn-primary" style={{ marginRight: '12px' }}>
              ⚙️ 去配置
            </button>
            <button onClick={() => navigate(`/monitor/data/${selectedProject}`)} className="btn-secondary">
              📝 去录入
            </button>
          </div>
        </div>
      )}

      {/* 场景状态卡片 - KIMI kpi-card 风格 */}
      {/* 根据 dashboardLayout 控制显示，未配置则默认全显示 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'statusCard')) && todayStatus && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">📍 今日状态</h2>
            <span className="section-badge">{todayStatus.date}</span>
          </div>
          <div className="kpi-cards-grid" style={{ marginTop: '8px' }}>
            {todayStatus.scenarios.map(sc => {
              const l = LIGHT_COLORS[sc.trafficLight] || LIGHT_COLORS.gray;
              const trend = sc.improvementRate != null ? (
                <div className={`kpi-trend ${sc.isBetter ? 'up' : 'down'}`}>
                  <span>{sc.isBetter ? '↑' : '↓'} {Math.abs(sc.improvementRate)}%</span>
                  <span className="baseline">较基线</span>
                </div>
              ) : null;

              // 同比趋势
              const yoyTrend = sc.yoyImprovementRate != null ? (
                <div className={`kpi-yoy ${sc.yoyIsBetter ? 'better' : 'worse'}`}>
                  <span className="yoy-label">同比</span>
                  <span className="yoy-value">
                    {sc.yoyIsBetter ? '↑' : '↓'} {Math.abs(sc.yoyImprovementRate)}%
                  </span>
                </div>
              ) : null;

              return (
                <div key={sc.id} className="kpi-card" style={{ borderLeft: `4px solid ${l.border}` }}>
                  <div className="kpi-header">
                    <div className="kpi-title">
                      <span className="kpi-icon" style={{ backgroundColor: l.bg }}>
                        {l.emoji}
                      </span>
                      <span className="kpi-name">{sc.name}</span>
                    </div>
                    <span className="kpi-status" style={{ color: l.text }}>{l.label}</span>
                  </div>
                  <div className="kpi-body">
                    <div className="kpi-value">
                      {fmt(sc.avgResponseSec)}<span className="kpi-unit">s</span>
                    </div>
                    <div className="kpi-meta">
                      <span className="meta-item">🧑‍💻 {fmt(sc.avgOnline)}人<span style={{ color: "#9ca3af" }}> 在线</span></span>
                      <span className="meta-item">📏 {sc.baselineSec > 0 ? `${sc.baselineSec}s` : '自动'}<span style={{ color: "#9ca3af" }}> 基准</span></span>
                    </div>
                  </div>
                  {yoyTrend}
                  {trend}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 迁移前后对比 */}
      {/* 迁移前后对比 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'comparisonTable')) && comparison?.scenarios && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">🔄 迁移前后对比</h2>
            {comparison.migrationDate && (
              <span className="section-badge info">{comparison.migrationDate} {comparison.migrationDesc}</span>
            )}
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ borderRight: '2px solid #e5e7eb', verticalAlign: 'middle' }}>场景</th>
                  <th>迁移前平均响应时长(s)</th>
                  <th>迁移后平均响应时长(s)</th>
                  <th>提升率</th>
                </tr>
              </thead>
              <tbody>
                {comparison.scenarios.map(sc => (
                  <tr key={sc.id}>
                    <td className="cell-bold">{sc.name}</td>
                    <td>{fmt(sc.beforeAvg)}</td>
                    <td>{fmt(sc.afterAvg)}</td>
                    <td>
                      {sc.improvementRate != null ? (
                        <span className={`trend-badge ${sc.isBetter ? 'success' : 'danger'}`}>
                          {sc.isBetter ? '↑' : '↓'} {Math.abs(sc.improvementRate)}% {sc.isBetter ? '提升' : '下降'}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 近7天趋势图 */}
      {/* 近7天趋势图 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'weeklyChart')) && weeklyData && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">📈 近7天趋势</h2>
            <span className="section-hint">柱状=在线人数 / 折线=响应时间</span>
          </div>
          <div className="chart-container" style={{ height: '420px' }}>
            <div ref={weeklyChartRef} style={{ height: '100%', width: '100%' }}></div>
          </div>
        </div>
      )}

      {/* 近7天系统响应速度 - 红绿灯表格 */}
      {/* 近7天系统响应速度 - 红绿灯表格 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'trafficGrid')) && gridData && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">📊 近7天系统响应速度</h2>
            <span className="section-hint">行=场景，列=日期×时点，颜色=响应状态</span>
          </div>
          {buildGridTable()}
        </div>
      )}
      {/* 近7日在线人数 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'onlineBar')) && weeklyData && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">📊 近7日在线人数</h2>
          </div>
          <div ref={onlineBarRef} style={{ height: '300px' }}></div>
        </div>
      )}

      {/* 响应时间分布饼图 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'pieChart')) && responseDist && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">🥧 响应时间分布</h2>
          </div>
          <div ref={pieChartRef} style={{ height: '300px' }}></div>
        </div>
      )}

      {/* 月度平均响应时间 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'monthlyAvg')) && monthlyAvgData && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">📈 月度平均响应时间</h2>
          </div>
          <div ref={monthlyAvgRef} style={{ height: '300px' }}></div>
        </div>
      )}

      {/* 全年各月日均在线人数 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'monthlyAvgBar')) && monthlyAvgOnline && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">📊 全年各月日均在线</h2>
          </div>
          <div ref={monthlyAvgBarRef} style={{ height: '300px' }}></div>
        </div>
      )}

      {/* 全年各时段平均在线人数 */}
      {(!config?.dashboardLayout || config.dashboardLayout.some(i => i.type === 'monthlyGroupBar')) && monthlyTpAvg && (
        <div className="section-block">
          <div className="section-header">
            <h2 className="section-title">📊 全年各时段平均在线</h2>
          </div>
          <div ref={monthlyGroupBarRef} style={{ height: '300px' }}></div>
        </div>
      )}

      {/* AI智能分析 */}
      <div style={{ marginTop: 16 }}>
        <AiAnalysisCard projectId={selectedProject} date={selectedDate} data={{ todayStatus, weeklyData, responseDist, monthlyAvgData, monthlyAvgOnline, monthlyTpAvg }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, marginBottom: 8 }}>
        <button onClick={loadAllData} className="btn-primary" style={{ fontSize: '12px', padding: '6px 16px' }} disabled={loading}>🔄 刷新</button>
      </div>
    </div>
  );
}

// FORCE_REBUILD_1780115613
